"use client";

import { FC } from "react";
import { Content, isFilled } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import Image from "next/image";
import customPageLeftShape from "@/assets/common/customPageLeftShape.svg";
import customPageRightShape from "@/assets/common/customPageRightShape.svg";
import noise from "@/assets/noise.png";
import { motion } from "framer-motion";
import { PrismicNextLink } from "@prismicio/next";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import clsx from "clsx";

/**
 * Props for `CustomPageHeader`.
 */
export type CustomPageHeaderProps =
  SliceComponentProps<Content.CustomPageHeaderSlice>;

/**
 * Component for "CustomPageHeader" Slices.
 */
const CustomPageHeader: FC<CustomPageHeaderProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden mt-[104px] h-[404px] bg-[#033D61]"
    >
      <div
        className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4]"
        style={{
          background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
        }}
      />
      <Image
        src={customPageLeftShape.src}
        alt="left shape"
        width={710.44}
        height={659.7}
        className="absolute left-0 md:-left-32 h-[404px] w-auto"
        priority
        fetchPriority="high"
      />
      <Image
        src={customPageRightShape.src}
        alt="right shape"
        width={710.44}
        height={659.7}
        className="absolute right-0 h-[404px] w-auto hidden md:block"
        priority
        fetchPriority="high"
      />
      <div className="absolute bg-[#43D9BD] blur-[137px] h-[275px] w-[275px] rounded-full right-[-125px] top-[-100px]" />
      <div className="absolute bg-[#43D9BD] blur-[102px] h-[260px] w-[260px] rounded-full bottom-[-130px] left-[-97px]" />
      <Bounded
        as="div"
        className="flex relative text-white h-full text-center flex-1 flex-col items-center justify-center gap-4"
      >
        <motion.div
          className="text-sm font-normal text-[#EBF7FF] font-merriweather leading-5 tracking-[0%] mb-8"
          initial={{
            opacity: 0,
            x: -10,
          }}
          animate={{
            opacity: 1,
            x: 0,
          }}
          transition={{
            duration: 0.4,
          }}
        >
          {slice.primary.breadcrumb_items.map((breadcrumbItem) => (
            <span key={breadcrumbItem.link.text}>
              <PrismicNextLink field={breadcrumbItem.link}>
                {breadcrumbItem.link.text}
              </PrismicNextLink>{" "}
              /{" "}
            </span>
          ))}
          <span>{slice.primary.current_page_breadcrumb_title}</span>
        </motion.div>
        <motion.h1
          className="font-archivo font-semibold text-5xl md:text-6xl leading-16 tracking-[0%] text-center mb-4"
          initial={{
            opacity: 0,
            y: 30,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.5,
            delay: 0.3,
          }}
        >
          {slice.primary.title}
        </motion.h1>
        <motion.p
          className="font-merriweather font-normal text-xl leading-8 tracking-[0%] text-center mb-8"
          initial={{
            opacity: 0,
            y: 30,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.8,
            delay: 0.8,
          }}
        >
          {slice.primary.description}
        </motion.p>
        {isFilled.group(slice.primary.action_buttons) && (
          <motion.div className="flex items-center justify-center gap-4" initial={{
            opacity: 0,
            y: 50,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.8,
            delay: 1.2,
          }}>
            {slice.primary.action_buttons.map((buttonComp) => (
              <PrismicNextLink
                key={buttonComp.button_url.text}
                field={buttonComp.button_url}
                scroll
              >
                <Button
                  className={clsx(`px-4 py-3.5 h-[52px] cursor-pointer font-archivo gap-1.5 text-base font-medium leading-6 rounded-[40px]`, buttonComp.button_url.variant === "Primary" ? "bg-[linear-gradient(264deg,#0798F2_-29%,#43D9BD_85.45%)] hover:bg-[linear-gradient(264deg,#43D9BD_-29%,#0798F2_85.45%)] text-[#033D61]" : "text-[#EBF7FF]! bg-transparent border border-[#EBF7FF] hover:bg-[#EBF7FF] hover:text-primary!")}
                  variant={"main"}
                >
                  <span className="whitespace-nowrap text-center">
                    {buttonComp.button_url.text}
                  </span>
                  {buttonComp.button_url.variant === "Primary" && <ArrowRight className="group-hover:hidden size-8! shrink-0 p-1 rounded-full text-primary!" />}
                </Button>
              </PrismicNextLink>
            ))}
          </motion.div>
        )}
      </Bounded>
    </section>
  );
};

export default CustomPageHeader;
